import type { ContentType } from "@brookbush/database";
import { UserAction } from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";
import clsx from "clsx";
import { motion } from "framer-motion";
import { MinusIcon, PlusIcon } from "lucide-react";

import { useBookmarks, useUserAction } from "~/hooks";
import useTrackCTA from "~/hooks/useTrackCTA";
import { buttonVariants } from "~/lib/animations.lib";

interface BookmarkButtonProps {
	contentId: string;
	contentType: ContentType;
	isIconButton?: boolean;
	id?: string;
}

const BookmarkButton = (props: BookmarkButtonProps) => {
	const { userId, isLoaded } = useAuth();
	const trackCTA = useTrackCTA();

	const { bookmarks, isLoadingBookmarks, addBookmark, removeBookmark } =
		useBookmarks({
			contentType: props.contentType,
			excludeContent: true,
		});

	const bookmark = bookmarks?.find(
		(bookmark) =>
			bookmark.userId === userId && bookmark.contentId === props.contentId,
	);

	const isLoading = isLoadingBookmarks || !isLoaded;

	const { setUserAction } = useUserAction();

	return (
		<motion.button
			disabled={isLoading}
			whileTap={buttonVariants.loading}
			id={props.id}
			className={clsx(
				"flex h-min max-w-max shrink-0 items-center justify-center gap-0.5 phablet:gap-1 rounded-full phablet:px-3 px-4 phablet:py-1.5 py-2 font-medium text-base disabled:opacity-75 disabled:brightness-100",
				bookmark
					? "bg-sky-100 text-sky-800 hover:bg-sky-200 dark:bg-sky-400/20 dark:text-sky-200 dark:hover:bg-sky-400/30"
					: "bg-slate-100 text-slate-800 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-50 dark:hover:bg-slate-600",
			)}
			onClick={() => {
				if (!userId) {
					trackCTA(UserAction.SIGN_UP, "Add To List");

					setUserAction(UserAction.SIGN_UP);
				} else if (!bookmark) {
					addBookmark(props.contentId);
				} else {
					removeBookmark(bookmark.id!);
				}
			}}
		>
			{bookmark ? (
				<>
					<MinusIcon className="phablet:-ml-0.5 h-5 w-5 shrink-0" />
					<span className="phablet:block hidden"> Remove From List</span>
				</>
			) : (
				<>
					<PlusIcon className="phablet:-ml-0.5 h-5 w-5 shrink-0" />
					<span className="phablet:block hidden"> Add To List</span>
				</>
			)}
		</motion.button>
	);
};

export default BookmarkButton;
